/**
 * Local stand-in for MARA's chat store (vendor-ref/mara/app/ui/src/store/chat.js), with no network and no model.
 * Messages keep prod's shape ({ id, type: 'message' | 'tool', role, content, created_at, status }) so MessageList and
 * ToolMessage render them the way prod does. A chat plays one of two scripts:
 *  - a baked prompt (baked.ts): status 'Thinking...', a tool card for the scene tool, the ops applied through
 *    applyAgentResult (one undo step), the tool output, then the reply streamed in;
 *  - anything else: status 'Thinking...', then a reply that points to the full Nanome workspace (cta).
 * Chats live for the session only; Chat History lists them.
 */
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useLiteStore } from '@nanome-lite/state';
import { applyAgentResult } from './apply-result';
import { bakedPrompt, proteinComponents, SCENE_TOOL, type BakedPrompt } from './baked';

export interface ChatMessage {
  id: string;
  type: 'message' | 'tool';
  role: 'user' | 'agent';
  content: string;
  created_at: Date;
  status?: 'pending' | 'error' | 'done';
  /** Show the Open in Nanome button under the message. */
  cta?: boolean;
}

export interface Chat {
  id: string;
  title: string;
  created_at: Date;
  last_message_at: Date;
  messages: ChatMessage[];
}

export const FREE_FORM_REPLY = 'Free-form requests run in the full Nanome workspace, where MARA can also run tools like docking.';
export const NO_PROTEIN_REPLY = 'There is no protein in the scene yet. Load a structure from the Import tab and ask again.';

/** Pacing of the scripted exchange; tests set everything to 0. */
export const timing = { thinkMs: 700, toolMs: 900, respondMs: 300, wordMs: 35 };

let seq = 0;
const nextId = () => `m${Date.now().toString(36)}${(seq++).toString(36)}`;
const sleep = (ms: number) => (ms > 0 ? new Promise<void>(r => setTimeout(r, ms)) : Promise.resolve());

export const useMaraChat = defineStore('mara-chat', () => {
  const chats = ref<Chat[]>([]);
  const selectedId = ref<string | null>(null);
  /** prod's chat.status: 'Thinking...', 'Executing...', 'Responding...' or null */
  const status = ref<string | null>(null);
  const streaming = ref(false);

  const selected = computed(() => chats.value.find(c => c.id === selectedId.value) ?? null);
  const messages = computed(() => selected.value?.messages ?? []);
  const busy = computed(() => status.value !== null || streaming.value);
  /** Newest first, like prod's workspaceHistory. */
  const history = computed(() => chats.value
    .map((chat, i) => ({ chat, i }))
    .sort((a, b) => (b.chat.last_message_at.getTime() - a.chat.last_message_at.getTime()) || (b.i - a.i))
    .map(x => x.chat));

  function newChat() {
    if (busy.value) return;
    selectedId.value = null;
  }

  function setSelected(id: string) {
    if (busy.value) return;
    selectedId.value = id;
  }

  function deleteChat(id: string) {
    if (busy.value && id === selectedId.value) return;
    chats.value = chats.value.filter(c => c.id !== id);
    if (selectedId.value === id) selectedId.value = null;
  }

  function push(chat: Chat, m: Omit<ChatMessage, 'id' | 'created_at'>): ChatMessage {
    const msg: ChatMessage = { ...m, id: nextId(), created_at: new Date() };
    chat.messages.push(msg);
    chat.last_message_at = msg.created_at;
    // hand back the reactive copy so later edits re-render
    return chat.messages[chat.messages.length - 1]!;
  }

  async function stream(chat: Chat, text: string, extra: Partial<ChatMessage> = {}) {
    streaming.value = true;
    const msg = push(chat, { type: 'message', role: 'agent', content: '', status: 'pending', ...extra, cta: false });
    try {
      if (timing.wordMs <= 0) msg.content = text;
      else {
        const words = text.split(/(\s+)/);
        for (const w of words) { msg.content += w; if (w.trim()) await sleep(timing.wordMs); }
      }
      msg.status = 'done';
      if (extra.cta) msg.cta = true;
    }
    finally { streaming.value = false; }
  }

  function ensureChat(title: string): Chat {
    if (selected.value) return selected.value;
    const now = new Date();
    chats.value.push({ id: nextId(), title, created_at: now, last_message_at: now, messages: [] });
    const chat = chats.value[chats.value.length - 1]!;
    selectedId.value = chat.id;
    return chat;
  }

  async function runBaked(chat: Chat, prompt: BakedPrompt) {
    const lite = useLiteStore();
    status.value = 'Thinking...';
    await sleep(timing.thinkMs);
    const proteins = proteinComponents(lite.components);
    if (!proteins.length) {
      status.value = null;
      await stream(chat, NO_PROTEIN_REPLY);
      return;
    }
    status.value = null;
    const input = JSON.stringify({ query: prompt.text });
    const tool = push(chat, { type: 'tool', role: 'agent', content: `**Tool**: ${SCENE_TOOL}\n\n**Input**: ${input}`, status: 'pending' });
    await sleep(timing.toolMs);
    let output: string;
    try {
      const applied = await applyAgentResult(lite, { message: prompt.reply, ops: prompt.ops(proteins) }, { focusDelayMs: 0 });
      output = applied.description.length ? applied.description.join('\n') : 'No changes were needed.';
      tool.status = 'done';
    }
    catch (e) {
      output = e instanceof Error ? e.message : String(e);
      tool.status = 'error';
    }
    tool.content += `\n\n**Output**: ${output}`;
    status.value = 'Responding...';
    await sleep(timing.respondMs);
    status.value = null;
    await stream(chat, tool.status === 'error' ? 'That did not work here. Try again, or open the scene in Nanome.' : prompt.reply, tool.status === 'error' ? { cta: true } : {});
  }

  async function runFreeForm(chat: Chat) {
    status.value = 'Thinking...';
    await sleep(timing.thinkMs);
    status.value = null;
    await stream(chat, FREE_FORM_REPLY, { cta: true });
  }

  /** chat.send: returns once the scripted exchange has finished. */
  async function send(text: string) {
    const t = text.trim();
    if (!t || busy.value) return;
    const chat = ensureChat(t);
    push(chat, { type: 'message', role: 'user', content: t, status: 'done' });
    const baked = bakedPrompt(t);
    try {
      if (baked) await runBaked(chat, baked);
      else await runFreeForm(chat);
    }
    finally { status.value = null; streaming.value = false; }
  }

  return { chats, selectedId, selected, messages, status, streaming, busy, history, newChat, setSelected, deleteChat, send };
});
